/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Col,
  Dropdown,
  Form,
  Input,
  Menu,
  Modal,
  Row,
  Select,
  Tooltip,
  Typography,
  notification,
} from 'antd';
import { EditOutlined, MinusCircleOutlined, MoreOutlined, CheckCircleOutlined } from '@ant-design/icons';
import axiosConfig from '../../services/axiosConfig';
import CommonAGGridTable from '../AG Grid/CommonAGGridTable';

const { Option } = Select;
const { Text, Title } = Typography;

const MATERIAL_TYPES = ['coating', 'welding'];

const multiFilter = {
  filter: 'agMultiColumnFilter',
  filterParams: {
    filters: [
      {
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains', 'equals', 'startsWith', 'endsWith'],
          buttons: ['reset'],
        },
      },
      {
        filter: 'agSetColumnFilter',
        filterParams: { buttons: ['reset'] },
      },
    ],
  },
};

/* ─────────────────────────────────────────────────────────────────────────────
   Generic master admin page – used for both Consumable and Material
───────────────────────────────────────────────────────────────────────────── */
const MasterAdmin = ({ endpoint, title }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [columnDefs, setColumnDefs] = useState([]);
  const [form] = Form.useForm();

  /* ── Fetch list ── */
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosConfig.get(`/${endpoint}/all`);
      const items = res?.data?.data ?? res?.data ?? [];
      const list = Array.isArray(items) ? items : [];
      setData(list.map((item, i) => ({ ...item, sno: i + 1 })));
    } catch {
      notification.error({ message: `Failed to load ${title} list` });
    } finally {
      setLoading(false);
    }
  }, [endpoint, title]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /* ── PUT { status } ── */
  const handleStatusChange = useCallback(
    async (record, nextStatus) => {
      if (!record?.id) return;
      setStatusUpdatingId(record.id);
      try {
        await axiosConfig.put(`/${endpoint}/${record.id}`, { status: nextStatus });
        notification.success({
          message: `${title} marked as ${nextStatus === 'active' ? 'Active' : 'Inactive'}`,
        });
        await fetchData();
      } catch {
        notification.error({ message: `Failed to update ${title} status` });
      } finally {
        setStatusUpdatingId(null);
      }
    },
    [endpoint, title, fetchData]
  );

  /* ── Open Add ── */
  const handleAdd = useCallback(() => {
    setEditRecord(null);
    form.resetFields();
    form.setFieldsValue({ status: 'active' });
    setModalVisible(true);
  }, [form]);

  /* ── Open Edit ── */
  const handleEdit = useCallback(
    (record) => {
      setEditRecord(record);
      form.setFieldsValue({
        category: record.category ?? '',
        name: record.name ?? '',
        productCode: record.productCode ?? '',
        unit: record.unit ?? '',
        materialType: record.materialType ?? undefined,
        status: (record.status || 'active').toLowerCase() === 'inactive' ? 'inactive' : 'active',
      });
      setModalVisible(true);
    },
    [form]
  );

  /* ── Build AG Grid column defs ── */
  useEffect(() => {
    setColumnDefs([
      {
        headerName: 'S.No',
        field: 'sno',
        maxWidth: 80,
        headerClass: 'ag-center-header',
        cellStyle: { textAlign: 'center' },
        suppressMovable: true,
        lockPosition: true,
        filter: false,
      },
      {
        headerName: 'Category',
        field: 'category',
        width: 180,
        cellStyle: { textAlign: 'left' },
        suppressMovable: true,
        ...multiFilter,
      },
      {
        headerName: 'Name',
        field: 'name',
        flex: 1,
        minWidth: 180,
        cellStyle: { textAlign: 'left' },
        suppressMovable: true,
        ...multiFilter,
      },
      {
        headerName: 'Product Code',
        field: 'productCode',
        width: 170,
        cellStyle: { textAlign: 'left' },
        suppressMovable: true,
        ...multiFilter,
      },
      {
        headerName: 'Unit',
        field: 'unit',
        width: 130,
        cellStyle: { textAlign: 'left' },
        suppressMovable: true,
        ...multiFilter,
        cellRenderer: (params) => params.value || '-',
      },
      {
        headerName: 'Material Type',
        field: 'materialType',
        width: 150,
        cellStyle: { textAlign: 'left' },
        suppressMovable: true,
        filter: 'agSetColumnFilter',
        filterParams: { buttons: ['reset'] },
        cellRenderer: (params) => {
          const val = params.value;
          if (!val) return '-';
          return val.charAt(0).toUpperCase() + val.slice(1);
        },
      },
      {
        headerName: 'Status',
        field: 'status',
        width: 130,
        cellStyle: { textAlign: 'center' },
        suppressMovable: true,
        filter: 'agSetColumnFilter',
        filterParams: { buttons: ['reset'] },
        cellRenderer: (params) => {
          const record = params.data;
          if (!record) return null;
          const isActive = (record.status || 'active').toLowerCase() === 'active';
          return isActive ? 'Active' : 'Inactive';
        },
      },
      {
        headerName: 'Actions',
        field: 'actions',
        width: 110,
        cellStyle: { textAlign: 'center' },
        suppressMovable: true,
        lockPosition: true,
        pinned: 'right',
        filter: false,
        cellRenderer: (params) => {
          const record = params.data;
          if (!record) return null;
          const isActive = (record.status || 'active').toLowerCase() === 'active';
          const isUpdating = statusUpdatingId === record.id;

          const menu = (
            <Menu>
              <Menu.Item
                key="toggle"
                icon={isActive ? <MinusCircleOutlined /> : <CheckCircleOutlined />}
                onClick={() => handleStatusChange(record, isActive ? 'inactive' : 'active')}
                disabled={isUpdating}
              >
                {isUpdating
                  ? 'Updating...'
                  : isActive
                  ? 'Make Inactive'
                  : 'Make Active'}
              </Menu.Item>
            </Menu>
          );

          return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, height: '100%' }}>
              <Tooltip title="Edit">
                <EditOutlined
                  className="txtIcon"
                  style={{ fontSize: '18px' }}
                  onClick={() => handleEdit(record)}
                />
              </Tooltip>
              <Dropdown overlay={menu} trigger={['click']} placement="bottomRight">
                <Tooltip title="More options">
                  <MoreOutlined
                    className="txtIcon"
                    style={{ fontSize: '18px', cursor: 'pointer' }}
                  />
                </Tooltip>
              </Dropdown>
            </div>
          );
        },
      },
    ]);
  }, [handleEdit, handleStatusChange, statusUpdatingId]);

  /* ── Save (create or update) ── */
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      if (editRecord) {
        await axiosConfig.put(`/${endpoint}/${editRecord.id}`, values);
        notification.success({ message: `${title} updated successfully` });
      } else {
        await axiosConfig.post(`/${endpoint}`, values);
        notification.success({ message: `${title} added successfully` });
      }
      setModalVisible(false);
      form.resetFields();
      setEditRecord(null);
      await fetchData();
    } catch (err) {
      if (err?.errorFields) return;
      notification.error({ message: `Failed to save ${title}` });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setModalVisible(false);
    form.resetFields();
    setEditRecord(null);
  };

  return (
    <div className="h-100">
      {/* ── Header ── */}
      <div className="d-flex mt-0.5 justify-content-between tab-content-title">
        <div className="d-flex align-items-center">
          <Title className="mb-0" level={4}>
            {`List of ${title}s`}
          </Title>
        </div>
        <div className="text-right new-calibration">
          <Text onClick={handleAdd} className="ps-3 add-new-calibration" style={{ cursor: 'pointer' }}>
            <span className="icon-22">
              <svg>
                <use xlinkHref="#add" />
              </svg>
            </span>
            {`Add ${title}`}
          </Text>
        </div>
      </div>

      <div className="custom-line" />

      {/* ── AG Grid ── */}
      <Row gutter={24}>
        <Col span={24} style={{ padding: '0px 12px 0' }}>
          <CommonAGGridTable
            rowData={data}
            columnDefs={columnDefs}
            activeKeyLine={`List of ${title}s`}
            isRawListLoading={loading}
            loadingMessage={`Loading ${title}s...`}
          />
        </Col>
      </Row>

      {/* ── Add / Edit Modal ── */}
      <Modal
        className="pipeline-modal"
        title={editRecord ? `Edit ${title}` : `Add ${title}`}
        centered
        visible={modalVisible}
        onCancel={handleCancel}
        footer={[
          <Button key="cancel" onClick={handleCancel}>
            Cancel
          </Button>,
          <Button key="save" type="primary" loading={saving} onClick={handleSave}>
            {saving ? 'Saving...' : editRecord ? 'Update' : 'Add'}
          </Button>,
        ]}
        width="50vw"
      >
        <Form form={form} layout="vertical" autoComplete="off" name={`${endpoint}-form`}>
          <Row gutter={24}>
            <Col lg={12} xs={24}>
              <Form.Item
                label="Category"
                name="category"
                rules={[{ required: true, message: 'Category is required' }]}
              >
                <Input className="client-input" placeholder="Enter category" size="large" />
              </Form.Item>
            </Col>
            <Col lg={12} xs={24}>
              <Form.Item
                label="Name"
                name="name"
                rules={[{ required: true, message: 'Name is required' }]}
              >
                <Input className="client-input" placeholder="Enter name" size="large" />
              </Form.Item>
            </Col>
            <Col lg={12} xs={24}>
              <Form.Item
                label="Product Code"
                name="productCode"
                rules={[{ required: true, message: 'Product code is required' }]}
              >
                <Input className="client-input" placeholder="Enter product code" size="large" />
              </Form.Item>
            </Col>
            <Col lg={12} xs={24}>
              <Form.Item label="Unit" name="unit">
                <Input className="client-input" placeholder="e.g. Kg, Bag (25Kg)" size="large" />
              </Form.Item>
            </Col>
            <Col lg={12} xs={24}>
              <Form.Item
                label="Material Type"
                name="materialType"
                rules={[{ required: true, message: 'Material type is required' }]}
              >
                <Select className="client-select" placeholder="Select material type" size="large">
                  {MATERIAL_TYPES.map((t) => (
                    <Option key={t} value={t}>
                      {t.charAt(0).toUpperCase() + t.slice(1)}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col lg={12} xs={24}>
              <Form.Item
                label="Status"
                name="status"
                rules={[{ required: true, message: 'Status is required' }]}
              >
                <Select className="client-select" placeholder="Select status" size="large">
                  <Option value="active">Active</Option>
                  <Option value="inactive">Inactive</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   Named exports – each used as a separate top-level admin tab
───────────────────────────────────────────────────────────────────────────── */
export const ConsumableMasterAdmin = () => (
  <MasterAdmin endpoint="consumable-master" title="Consumable" />
);

export const MaterialMasterAdmin = () => (
  <MasterAdmin endpoint="material-master" title="Material" />
);
